import { useEffect, useRef } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/audit";

interface Props {
  homeCode: string;
  awayCode: string;
  homeFlag: string;
  awayFlag: string;
  homeTeamName?: string;
  awayTeamName?: string;
  homeScore: number;
  awayScore: number;
  /** Direction the home team is currently attacking. Default "right". */
  attackingDirection?: "left" | "right";
  variant?: "overlay" | "block";
  minute?: number;
  formationHome?: string;
  formationAway?: string;
  topPerformerName?: string;
  topPerformerTeam?: string;
}

/**
 * Broadcast-style team identity bar that sits above the pitch and clearly
 * communicates which team is on the LEFT vs RIGHT side, with flags, codes,
 * live score and attack-direction arrow. Mobile-first: collapses to flag+code.
 */
export function PitchTeamIdentityBar({
  homeCode,
  awayCode,
  homeFlag,
  awayFlag,
  homeTeamName,
  awayTeamName,
  homeScore,
  awayScore,
  attackingDirection = "right",
  variant = "overlay",
  minute,
  formationHome,
  formationAway,
  topPerformerName,
  topPerformerTeam,
}: Props) {
  const { t } = useLanguage();
  const tracked = useRef(false);
  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    void trackEvent("pitch_team_identity_rendered", { homeCode, awayCode });
    void trackEvent("attack_direction_displayed", { direction: attackingDirection });
    void trackEvent("team_identity_bar_expanded", {
      homeCode,
      awayCode,
      hasFormation: !!(formationHome && formationAway),
      hasTopPerformer: !!topPerformerName,
    });
  }, [homeCode, awayCode, attackingDirection, formationHome, formationAway, topPerformerName]);

  const wrap =
    variant === "overlay"
      ? "pointer-events-none absolute inset-x-0 top-0 z-20 flex items-center justify-between gap-2 bg-gradient-to-b from-background/85 via-background/40 to-transparent px-2 py-1.5 sm:px-3 sm:py-2 backdrop-blur-sm"
      : "relative flex items-center justify-between gap-2 rounded-2xl border border-border/40 glass px-2 py-1.5 sm:px-3 sm:py-2";

  return (
    <div
      className={wrap}
      role="group"
      aria-label={t("pitch_identity_legend", {
        leftLabel: t("pitch_left_side"),
        rightLabel: t("pitch_right_side"),
        home: homeCode,
        away: awayCode,
      })}
    >
      {/* Left / Home */}
      <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
        <span className="rounded-md bg-background/70 px-1.5 py-0.5 text-[8px] font-black tracking-widest text-muted-foreground">
          {t("pitch_home_label")}
        </span>
        <span className="text-base sm:text-lg leading-none" aria-hidden>
          {homeFlag}
        </span>
        <span className="text-[11px] sm:text-sm font-black tracking-tight">{homeCode}</span>
        {homeTeamName && (
          <span className="hidden md:inline truncate max-w-[120px] text-[10px] font-semibold text-muted-foreground">
            {homeTeamName}
          </span>
        )}
        {formationHome && (
          <span className="hidden lg:inline rounded-md bg-primary/10 px-1.5 py-0.5 text-[9px] font-black tracking-wider text-primary">
            {formationHome}
          </span>
        )}
      </div>

      {/* Center: score + attack arrow */}
      <div className="flex shrink-0 items-center gap-1.5">
        {attackingDirection === "left" ? (
          <ArrowLeft className="h-3 w-3 text-team animate-pulse-dot" aria-label={t("pitch_attack_direction")} />
        ) : null}
        <div className="flex flex-col items-center leading-none">
          <div className="accent-team-ring rounded-lg bg-background/80 px-2 py-0.5 text-[12px] sm:text-base font-black tabular-nums tracking-tighter">
            {homeScore}<span className="mx-1 text-muted-foreground/60">-</span>{awayScore}
          </div>
          {typeof minute === "number" && (
            <div className="mt-0.5 text-[8px] font-bold tracking-wider text-muted-foreground">
              {minute}' {t("pitch_minute")}
            </div>
          )}
        </div>
        {attackingDirection === "right" ? (
          <ArrowRight className="h-3 w-3 text-team animate-pulse-dot" aria-label={t("pitch_attack_direction")} />
        ) : null}
      </div>

      {/* Right / Away */}
      <div className="flex min-w-0 items-center justify-end gap-1.5 sm:gap-2">
        {formationAway && (
          <span className="hidden lg:inline rounded-md bg-primary/10 px-1.5 py-0.5 text-[9px] font-black tracking-wider text-primary">
            {formationAway}
          </span>
        )}
        {awayTeamName && (
          <span className="hidden md:inline truncate max-w-[120px] text-[10px] font-semibold text-muted-foreground">
            {awayTeamName}
          </span>
        )}
        <span className="text-[11px] sm:text-sm font-black tracking-tight">{awayCode}</span>
        <span className="text-base sm:text-lg leading-none" aria-hidden>
          {awayFlag}
        </span>
        <span className="rounded-md bg-background/70 px-1.5 py-0.5 text-[8px] font-black tracking-widest text-muted-foreground">
          {t("pitch_away_label")}
        </span>
      </div>
      {topPerformerName && (
        <div className="absolute -bottom-3 left-1/2 hidden -translate-x-1/2 rounded-full bg-background/90 px-2 py-0.5 text-[9px] font-bold tracking-wide text-primary shadow md:block">
          ⭐ {t("pitch_top_performer")}: {topPerformerName}
          {topPerformerTeam ? ` · ${topPerformerTeam}` : ""}
        </div>
      )}
    </div>
  );
}